import Div from "@jumbo/shared/Div/Div";
import DifferenceOutlinedIcon from "@mui/icons-material/DifferenceOutlined";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import FormSearchFilter from "app/pages/Docgen/components/common/form-search-filter/FormSearchFilter";
import MuiTableWrapper from "app/pages/Docgen/components/common/mui-table-wrapper/MuiTableWrapper";
import {useEffect, useState} from "react";
import {get} from "../../../common/network";
import {Date_format, DateFormatter, formatDate} from "../../../constants/date_format";

const HistoryTab = ({id, isXfa}) => {
    const [selectedRows, setSelectedRows] = useState([]); // Array of the selected rows
    const [dataModal, setDataModal] = useState(false);
    const [formDataModal, setFormDataModal] = useState(false);
    const [historyId, setHistoryId] = useState("");
    const [csvHistoryId, setCsvHistoryId] = useState("");
    const [getUpdatedBy, setUpdatedBy] = useState("");
    const [getUpdatedAt, setUpdatedAt] = useState("");
    const [data, setData] = useState([]);
    const [historyData, setHistoryData] = useState([]);
    const [formHistoryData, setFormHistoryData] = useState([]);
    const [isFetching, setIsFetching] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const [formTypeValue, setFormTypeValue] = useState("");
    const [dateValue, setDateValue] = useState("");
    const [formHistoryLoading, setFormHistoryLoading] = useState(false);
    const [csvHistoryLoading, setCsvHistoryLoading] = useState(false);
    const [rowsSelection, setRowsSelection] = useState([]);

    const handleDataModalClose = () => {
        setDataModal(false);
    };

    const handleFormDataModalClose = () => {
        setFormDataModal(false);
    };

    const columns = [
        {
            field: "formCode",
            header: "Form Name"
        },
        {
            field: "type",
            header: "Change Type"
        },
        {
            field: "updated_by",
            header: "Updated By",
            render: (value, row) => {
                const first_name = row?.user?.first_name;
                const last_name = row?.user?.last_name;
                return `${first_name} ${last_name}`;
            }
        },
        {
            field: "updated_at",
            header: "Update Date",
            render: value => {
                const date = new Date(value); // Convert the ISO date string to a Date object
                return DateFormatter(date);
            }
        },
        {
            field: "view_change",
            header: "View Data Change",
            render: (value, row) => {
                return (
                    <Button
                        variant="text"
                        className="listing-btn"
                        sx={{
                            textDecoration: "none !important"
                        }}
                        onClick={() => {
                            const first_name = row?.user?.first_name;
                            const last_name = row?.user?.last_name;
                            setUpdatedBy(`${first_name} ${last_name}`);

                            setUpdatedAt(row.updated_at);
                            if (row.gen_form_id) {
                                setHistoryId(row.id);
                                setFormDataModal(true);
                            } else {
                                setCsvHistoryId(row.id);
                                setDataModal(true);
                            }
                        }}
                    >
                        <DifferenceOutlinedIcon
                            sx={{
                                fontSize: "20px",
                                color: "inherit",
                                mr: "4px"
                            }}
                        />{" "}
                        View
                    </Button>
                );
            }
        }
    ];

    const handleSearch = value => {
        const columnName = value?.value || ""; // Name of the column
        const searchValue = value?.searchedText || "";

        const dropdownValue = value?.dropdownValue || ""; // value selected in the dropdown field
        setSearchValue(searchValue);
        setFilterText(columnName);
        setFormTypeValue(dropdownValue);

        if (columnName === "updated_at") {
            setDateValue(value);
        }
    };

    const fetchData = async () => {
        setIsFetching(true);
        try {
            const [response1, response2] = await Promise.all([get(`docgen/matterhistory/${id}`), get(`docgen/generated-forms/history/${id}`)]);

            const rows1 = response1?.data ?? [];
            const rows2 = response2?.data ?? [];

            // Modify each row to include the old and new values
            const rowsWithChanges1 = rows1.map(row => ({
                ...row,
                old_value: JSON.parse(row?.old_value),
                new_value: JSON.parse(row?.new_value)
            }));

            const rowsWithChanges2 = rows2.map(row => {
                try {
                    return {
                        ...row,
                        old_value: JSON.parse(row?.previous_value),
                        new_value: JSON.parse(row?.new_value)
                    };
                } catch (error) {
                    // You can choose to return the original row, or modify it as needed
                    return {
                        ...row,
                        old_value: null,
                        new_value: null
                    };
                }
            });

            // Combine the rows from both responses
            let combinedRows = [...rowsWithChanges1, ...rowsWithChanges2];
            setIsFetching(false);
            setData(combinedRows);
        } catch (error) {
            setIsFetching(false);
        }
    };

    const historyColumns = [
        {
            field: "field_name",
            header: "Docket Field Name"
        },
        {
            field: "old_val",
            header: "Old Value",
            render: value => {
                // Check if the value matches either ISO 8601 or date-only format
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                        return DateFormatter(date); // Format the ISO 8601 date
                    }
                } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    // Handle date-only format
                    return formatDate(value, true);
                }

                return value; // Return the original value if it doesn't match either format
            }
        },
        {
            field: "new_val",
            header: "New Value",
            render: value => {
                // Check if the value matches either ISO 8601 or date-only format
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                        return DateFormatter(date); // Format the ISO 8601 date
                    }
                } else if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                    // Handle date-only format
                    return formatDate(value, true);
                }

                return value; // Return the original value if it doesn't match either format
            }
        },
        {
            field: "updated_at",
            header: "Date",
            render: value => {
                return DateFormatter(new Date(value));
            }
        },
        {
            field: "updated_by",
            header: "Updated by"
        }
    ];

    const formHistoryColumns = [
        {
            field: "field_name",
            header: "Form Field Name",
            render: value => <span style={{wordBreak: "break-all"}}>{value}</span>
        },
        {
            field: "old_val",
            header: "Old Value",
            render: value => {
                // Check if the value matches either ISO 8601 or date-only format
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                        return DateFormatter(date); // Format the ISO 8601 date
                    }
                }

                return value; // Return the original value if it doesn't match either format
            }
        },
        {
            field: "new_val",
            header: "New Value",
            render: value => {
                // Check if the value matches either ISO 8601 or date-only format
                if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)) {
                    const date = new Date(value);
                    if (!isNaN(date)) {
                        return DateFormatter(date); // Format the ISO 8601 date
                    }
                }

                return value; // Return the original value if it doesn't match either format
            }
        },
        {
            field: "updated_at",
            header: "Date",
            render: value => {
                return DateFormatter(new Date(value));
            }
        },
        {
            field: "updated_by",
            header: "Updated by"
        }
    ];

    const fetchHistoryData = async () => {
        setCsvHistoryLoading(true);
        try {
            const response = csvHistoryId && await get(`docgen/matterhistorydetails/${csvHistoryId}`);

            const rows = response?.data ?? [];

            // Modify each row to include the old and new values
            const rowsWithChanges = rows.map(row => {
                const formattedRow = {};

                for (const [key, value] of Object.entries(row)) {
                    formattedRow.field_name = key;

                    // Check if key ends with "__deleted"
                    if (key.endsWith("__deleted")) {
                        // Remove the "__deleted" part from the key
                        formattedRow.field_name = key.replace(/__deleted$/, "");

                        // Show only the value, replacing value.__new if available
                        formattedRow.old_val = value;
                        formattedRow.new_val = undefined; // Set undefined to exclude it
                    } else if (key.endsWith("__added")) {
                        // Remove the "__deleted" part from the key
                        formattedRow.field_name = key.replace(/__added$/, "");

                        // Show only the value, replacing value.__new if available
                        formattedRow.old_val = undefined;
                        formattedRow.new_val = value; // Set undefined to exclude it
                    } else {
                        // For other keys, use value.__old if available, else use the value
                        formattedRow.old_val = value.__old;
                        formattedRow.new_val = value.__new;
                    }

                    formattedRow.updated_by = getUpdatedBy;
                    formattedRow.updated_at = getUpdatedAt;
                }
                return formattedRow;
            });
            setHistoryData(rowsWithChanges);
        } catch (error) {
        } finally {
            setCsvHistoryLoading(false);
        }
    };
    //#endregion

    const fetchFormHistoryData = async () => {
        setFormHistoryLoading(true);
        try {
            const response = historyId && await get(`docgen/generated-forms/history-details/${historyId}`);
            const rows = response?.data || [];

            const modifiedRows = rows.map(item => convertData(item));
            Object.keys(modifiedRows[0]).forEach(item => {
                const keyValueObject = {};
                keyValueObject[item] = modifiedRows[0][item];
            });

            // Modify each row to include the old and new values
            const rowsWithChanges = rows.map(row => {
                const formattedRow = {};

                for (const [key, value] of Object.entries(row)) {
                    formattedRow.field_name = key;
                    // Check if key ends with "__deleted"
                    if (key.endsWith("__deleted") || key.includes("__deleted")) {
                        // Remove the "__deleted" part from the key
                        formattedRow.field_name = key.replace(/__deleted$/, "");

                        // Show only the value, replacing value.__new if available
                        formattedRow.old_val = value;
                        formattedRow.new_val = undefined; // Set undefined to exclude it
                    } else if (key.endsWith("__added") || key.includes("_added")) {
                        formattedRow.field_name = key.replace(/__added$/, "");

                        // Show only the value, replacing value.__new if available
                        formattedRow.old_val = undefined;
                        formattedRow.new_val = value; // Set undefined to exclude it
                    } else {
                        // For other keys, use value.__old if available, else use the value
                        formattedRow.old_val = value.__old;
                        formattedRow.new_val = value.__new;

                        if (key.includes("__old")) {
                            formattedRow.field_name = key.replace(/.__old/g, "");
                            formattedRow.old_val = value;
                        }

                        if (key.includes("__new")) {
                            formattedRow.field_name = key.replace(/.__new/g, "");
                            formattedRow.new_val = value;
                        }
                    }

                    formattedRow.updated_by = getUpdatedBy;
                    formattedRow.updated_at = getUpdatedAt;
                }

                return formattedRow;
            });

            let mergedArray = rowsWithChanges.reduce((acc, curr) => {
                let existingIndex = acc.findIndex(item => item.field_name.replace(/.__old/g, "").replace(/.__new/g, "") === curr.field_name.replace(/.__old/g, "").replace(/.__new/g, ""));

                if (existingIndex !== -1) {
                    acc[existingIndex].old_val = acc[existingIndex].old_val || curr.old_val;
                    acc[existingIndex].new_val = acc[existingIndex].new_val || curr.new_val;
                    acc[existingIndex].field_name = acc[existingIndex].field_name.replace(/.__old/g, "").replace(/.__new/g, "");
                } else {
                    acc.push({...curr, field_name: curr.field_name.replace(/.__old/g, "").replace(/.__new/g, "")});
                }

                return acc;
            }, []);

            setFormHistoryData(mergedArray);
        } catch (error) {
        } finally {
            setFormHistoryLoading(false);
        }
    };

    const convertData = (obj, parentKey = "", converted = {}) => {
        for (let key in obj) {
            if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
                convertData(obj[key], `${parentKey}${key}.`, converted);
            } else if (Array.isArray(obj[key])) {
                obj[key].forEach((item, index) => {
                    convertData(item, `${parentKey}${key}[${index}].`, converted);
                });
            } else {
                converted[`${parentKey}${key}`] = obj[key];
            }
        }
        return converted;
    };

    useEffect(() => {
        fetchData();
        handleSearch();
    }, []);

    useEffect(() => {
        fetchFormHistoryData(isXfa);
    }, [historyId]);

    useEffect(() => {
        fetchHistoryData();
    }, [csvHistoryId]);


    return (
        <div className="page-content-wrapper">
            <Box className="w-100">
                <header className="section-heading">
                    <div className="left-area">
                        <FormSearchFilter
                            options={[
                                {value: "all", label: "All"},
                                {value: "type", label: "Change Type"},
                                {value: "updated_by", label: "Updated By"},
                                {value: "updated_at", label: "Update Date"}
                            ]}
                            defaultOption={{value: "", label: "Select an option"}}
                            dateFiltersOptionsValueArray={["updated_at"]}
                            onSearch={values => handleSearch(values)}
                        />
                    </div>
                </header>
                <div>
                    <MuiTableWrapper
                        columns={columns}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        hasCheckboxesColumn={false}
                        searchValue={searchValue}
                        filterColumn={filterText}
                        formTypeValue={formTypeValue}
                        data={data}
                        isFetching={isFetching}
                        dateValue={dateValue}
                        rowsSelection={rowsSelection}
                        setRowsSelection={setRowsSelection}
                    />
                </div>
            </Box>
            <Modal open={dataModal} onClose={handleDataModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Div
                    sx={{
                        height: "60vh !important",
                        width: "100%",
                        maxWidth: "1000px"
                    }}
                    className="customize-modal"
                >
                    <div className="modal-head">
                        <h2>Changed Data</h2>
                        <IconButton className="close-modal-btn p-0" onClick={handleDataModalClose}>
                            <HighlightOffIcon/>
                        </IconButton>
                    </div>
                    <div className="modal-body-content">
                        <div className="table-responsive">
                            <div id="history-tab" name="history-tab">
                                <MuiTableWrapper
                                    columns={historyColumns}
                                    data={historyData}
                                    selectedRows={[]}
                                    setSelectedRows={() => {
                                    }}
                                    hasCheckboxesColumn={false}
                                    colgroup={[
                                        {width: "20%"},
                                        {width: "20%"},
                                        {width: "20%"},
                                        {width: "20%"},
                                        {width: "20%"}
                                    ]}
                                    searchValue={""}
                                    filterColumn={"all"}
                                    isFetching={csvHistoryLoading}
                                    rowsSelection={rowsSelection}
                                    setRowsSelection={setRowsSelection}
                                />
                            </div>
                        </div>
                    </div>
                </Div>
            </Modal>
            <Modal open={formDataModal} onClose={handleFormDataModalClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Div
                    sx={{
                        height: "60vh !important",
                        width: "100%",
                        maxWidth: "1000px"
                    }}
                    className="customize-modal"
                >
                    <div className="modal-head">
                        <h2>Changed Data</h2>
                        <IconButton className="close-modal-btn p-0" onClick={handleFormDataModalClose}>
                            <HighlightOffIcon/>
                        </IconButton>
                    </div>
                    <div className="modal-body-content">
                        <div className="table-responsive">
                            <MuiTableWrapper
                                columns={formHistoryColumns}
                                data={formHistoryData}
                                searchValue={""}
                                filterColumn={"all"}
                                selectedRows={[]}
                                setSelectedRows={() => {
                                }}
                                hasCheckboxesColumn={false}
                                colgroup={[
                                    {width: "20%"},
                                    {width: "20%"},
                                    {width: "20%"},
                                    {width: "20%"},
                                    {width: "20%"}
                                ]}
                                isFetching={formHistoryLoading}
                                rowsSelection={rowsSelection}
                                setRowsSelection={setRowsSelection}
                            />
                        </div>
                    </div>
                </Div>
            </Modal>
        </div>
    );
};

export default HistoryTab;